import React from 'react';
import { Box, Chip, Icon, Paper, Skeleton, Stack, Typography, useTheme } from '@mui/material';
import Grid from '@mui/material/Grid2';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';

import { OnboardingComparisonData } from '@/services/onboardingComparisonService';

type TrialWelcomeComparisonTableProps = {
  comparisonData: OnboardingComparisonData | null;
  loading: boolean;
};

const TrialWelcomeComparisonTable: React.FC<TrialWelcomeComparisonTableProps> = ({ comparisonData, loading }) => {
  const theme = useTheme();

  const renderFeatureIcon = (iconName?: string) => {
    if (!iconName) return null;
    return (
      <Icon baseClassName="material-symbols-outlined" sx={{ fontSize: 20, color: theme.palette.primary.main }}>
        {iconName}
      </Icon>
    );
  };

  const renderFeatureValue = (value: boolean | string, isPro: boolean) => {
    if (typeof value === 'boolean') {
      return value ? (
        <CheckCircleOutlineIcon
          sx={{
            fontSize: 20,
            color: isPro ? theme.palette.success.main : theme.palette.neutral.greyBackground,
          }}
        />
      ) : (
        <CloseIcon sx={{ fontSize: 20, color: theme.palette.neutral.dashBorder }} />
      );
    }
    return (
      <Typography
        variant="body2"
        sx={{
          fontWeight: isPro ? 600 : 400,
          color: isPro ? theme.palette.text.primary : theme.palette.neutral.greyFont,
          textAlign: 'right',
          lineHeight: 1.4,
          py: 0.5,
          minHeight: 20,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          fontSize: { xs: '0.825rem', sm: '0.875rem' },
          whiteSpace: 'nowrap',
        }}
      >
        {value}
      </Typography>
    );
  };

  return (
    <Box sx={{ width: '100%', px: { xs: 0, md: 1 } }}>
      <Typography
        variant="h5"
        sx={{
          textAlign: 'center',
          mb: { xs: 3, md: 4 },
          fontWeight: 600,
          color: theme.palette.text.primary,
          fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' },
          px: { xs: 2, md: 0 },
        }}
      >
        See what you've unlocked with Pro
      </Typography>

      <Grid container spacing={{ xs: 2, md: 3 }}>
        <Grid size={{ xs: 12, md: 6 }} sx={{ order: { xs: 1, md: 2 } }}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 4 },
              height: '100%',
              borderRadius: 3,
              border: '2px solid',
              borderColor: theme.palette.primary.main,
              background: theme.palette.neutral.lightBackground,
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              },
            }}
          >
            <Stack spacing={3}>
              <Box>
                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: theme.palette.text.primary,
                      fontSize: { xs: '1rem', md: '1.25rem' },
                    }}
                  >
                    {comparisonData?.proTitle || 'Pro Plan'}
                  </Typography>
                  <Chip
                    label="CURRENT"
                    size="small"
                    sx={{
                      height: { xs: 20, sm: 24 },
                      fontSize: { xs: '0.65rem', sm: '0.75rem' },
                      fontWeight: 600,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                      color: 'white',
                    }}
                  />
                </Stack>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.primary.main,
                    fontWeight: 500,
                    fontSize: { xs: '0.825rem', sm: '0.875rem' },
                  }}
                >
                  {comparisonData?.proDescription || 'Your trial includes all these features'}
                </Typography>
              </Box>

              <Stack spacing={0}>
                {loading
                  ? Array.from({ length: 8 }).map((_, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          minHeight: 48,
                          py: 1,
                          gap: 2,
                        }}
                      >
                        <Skeleton variant="circular" width={20} height={20} />
                        <Skeleton variant="text" sx={{ flex: 1 }} />
                        <Skeleton variant="circular" width={20} height={20} />
                      </Box>
                    ))
                  : comparisonData?.features.map((feature, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          minHeight: { xs: 40, md: 48 },
                          py: { xs: 0.75, md: 1 },
                          gap: { xs: 1.5, md: 2 },
                          borderBottom:
                            index < comparisonData.features.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                          '& > *': {
                            animation: `fadeIn 0.5s ease-out ${index * 0.05}s both`,
                          },
                        }}
                      >
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          {renderFeatureIcon(feature.icon)}
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{
                            flex: 1,
                            fontWeight: 500,
                            color: theme.palette.text.primary,
                            lineHeight: 1.4,
                            py: 0.5,
                            fontSize: { xs: '0.825rem', sm: '0.875rem' },
                          }}
                        >
                          {feature.label}
                        </Typography>
                        <Box
                          sx={{
                            width: 80,
                            display: 'flex',
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                            flexShrink: 0,
                          }}
                        >
                          {renderFeatureValue(feature.pro, true)}
                        </Box>
                      </Box>
                    ))}
              </Stack>
            </Stack>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }} sx={{ order: { xs: 2, md: 1 } }}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 4 },
              height: '100%',
              borderRadius: 3,
              border: `2px solid ${theme.palette.neutral.otherBorder}`,
              background: theme.palette.background.default,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Stack spacing={3}>
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.neutral.greyFont,
                    mb: 1,
                    fontSize: { xs: '1rem', md: '1.25rem' },
                  }}
                >
                  {comparisonData?.freeTitle || 'Free Plan'}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.neutral.greyBackground,
                    fontSize: { xs: '0.825rem', sm: '0.875rem' },
                  }}
                >
                  {comparisonData?.freeDescription || 'What you had before'}
                </Typography>
              </Box>

              <Stack spacing={0}>
                {loading
                  ? Array.from({ length: 8 }).map((_, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          minHeight: 48,
                          py: 1,
                          gap: 2,
                        }}
                      >
                        <Skeleton variant="circular" width={20} height={20} />
                        <Skeleton variant="text" sx={{ flex: 1 }} />
                        <Skeleton variant="circular" width={20} height={20} />
                      </Box>
                    ))
                  : comparisonData?.features.map((feature, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          minHeight: { xs: 40, md: 48 },
                          py: { xs: 0.75, md: 1 },
                          gap: { xs: 1.5, md: 2 },
                          opacity: 0.7,
                          borderBottom:
                            index < comparisonData.features.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                        }}
                      >
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            opacity: 0.5,
                            flexShrink: 0,
                          }}
                        >
                          {renderFeatureIcon(feature.icon)}
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{
                            flex: 1,
                            color: theme.palette.neutral.greyFont,
                            lineHeight: 1.4,
                            py: 0.5,
                            fontSize: { xs: '0.825rem', sm: '0.875rem' },
                          }}
                        >
                          {feature.label}
                        </Typography>
                        <Box
                          sx={{
                            width: 80,
                            display: 'flex',
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                            flexShrink: 0,
                          }}
                        >
                          {renderFeatureValue(feature.free, false)}
                        </Box>
                      </Box>
                    ))}
              </Stack>
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TrialWelcomeComparisonTable;
